/**
 * Click menu for a work-item chip in an EDITABLE surface.
 *
 * A chip in read-only content is just a link — click it, the card opens.
 * While editing, a plain link is the wrong affordance: the author is more
 * likely to want to undo the chip than to leave the page, and a hard
 * navigation out of an editor with unsaved bytes is hostile. So in edit
 * mode the click opens this menu instead, which offers both.
 *
 * **Convert to text** is the reason this exists (MOTE-30's second half):
 * auto-detection means chips appear without being asked for, so there has
 * to be a one-click way back to plain text. It emits exactly the
 * reference the chip stands for (`MOTE-30`) — not `MOTE-30: Title`, which
 * would put a copy of a mutable title into the body.
 *
 * Positioning goes through the shared `resolvePopoverPosition` helper; see
 * its header for why a fixed popover must be given a KNOWN size rather
 * than measured after mounting.
 */

import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

import { resolvePopoverPosition } from '@/lib/anchor-popover'

const MENU_WIDTH_PX = 208
const ROW_HEIGHT_PX = 32
const CHROME_HEIGHT_PX = 8

export interface WorkItemChipMenuProps {
  anchor: DOMRect
  /** Absolute path to the card; omitted when the chip never resolved. */
  href: string | null
  onConvertToText: () => void
  onClose: () => void
}

export function WorkItemChipMenu({
  anchor,
  href,
  onConvertToText,
  onClose,
}: WorkItemChipMenuProps) {
  const { t } = useTranslation('wiki')
  const menuRef = useRef<HTMLDivElement>(null)
  const [copied, setCopied] = useState(false)

  const rowCount = href ? 3 : 1
  const position = resolvePopoverPosition({
    anchor,
    size: { width: MENU_WIDTH_PX, height: rowCount * ROW_HEIGHT_PX + CHROME_HEIGHT_PX },
  })

  // Focus the first action so the menu is reachable without a mouse.
  useLayoutEffect(() => {
    menuRef.current?.querySelector<HTMLElement>('[data-menu-item]')?.focus()
  }, [])

  useEffect(() => {
    // Capture phase: an ancestor <Modal> registers its own document-level
    // Escape listener FIRST (it was open before this menu mounted), and
    // same-phase listeners fire in registration order — so a bubble-phase
    // handler here would lose and Escape would close the whole card modal
    // instead of just this menu. See the [FE/Modal] pitfall.
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      event.preventDefault()
      event.stopPropagation()
      onClose()
    }
    const onPointerDown = (event: PointerEvent) => {
      if (menuRef.current?.contains(event.target as Node)) return
      onClose()
    }
    document.addEventListener('keydown', onKeyDown, true)
    document.addEventListener('pointerdown', onPointerDown, true)
    // A scroll moves the chip out from under the menu; re-anchoring a
    // click-opened menu mid-scroll reads as jitter, so close instead.
    window.addEventListener('scroll', onClose, true)
    return () => {
      document.removeEventListener('keydown', onKeyDown, true)
      document.removeEventListener('pointerdown', onPointerDown, true)
      window.removeEventListener('scroll', onClose, true)
    }
  }, [onClose])

  const copyLink = () => {
    if (!href) return
    void navigator.clipboard
      .writeText(`${window.location.origin}${href}`)
      .then(() => {
        setCopied(true)
        window.setTimeout(onClose, 700)
      })
      .catch(() => onClose())
  }

  const itemClass =
    'flex w-full items-center rounded px-2 py-1.5 text-start text-sm text-fg hover:bg-bg-muted focus-visible:bg-bg-muted focus-visible:outline-none'

  return createPortal(
    <div
      ref={menuRef}
      role="menu"
      aria-label={t('workItem.menuAriaLabel', { defaultValue: 'Work item reference actions' })}
      style={{ top: position.top, left: position.left, width: MENU_WIDTH_PX }}
      className="fixed z-50 rounded-md border border-border bg-bg-elevated p-1 shadow-lg"
    >
      {href ? (
        <>
          <a
            data-menu-item
            role="menuitem"
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className={`${itemClass} no-underline`}
            onClick={onClose}
          >
            {t('workItem.menuOpenCard', { defaultValue: 'Open card' })}
          </a>
          <button
            data-menu-item
            type="button"
            role="menuitem"
            className={itemClass}
            onClick={copyLink}
          >
            {copied
              ? t('workItem.menuLinkCopied', { defaultValue: 'Link copied' })
              : t('workItem.menuCopyLink', { defaultValue: 'Copy link' })}
          </button>
        </>
      ) : null}
      <button
        data-menu-item
        type="button"
        role="menuitem"
        className={itemClass}
        onClick={() => {
          onConvertToText()
          onClose()
        }}
      >
        {t('workItem.menuConvertToText', { defaultValue: 'Convert to text' })}
      </button>
    </div>,
    document.body,
  )
}

WorkItemChipMenu.displayName = 'WorkItemChipMenu'
